/**
 * Spin history: persistence and export.
 *
 * Results used to live only in component state, so closing the tab lost them.
 * That is the wrong trade for the two audiences who need them most — a teacher
 * recording who was called on, and a raffle host who has to show their working
 * afterwards.
 */

export interface ResultEntry {
  name: string;
  /** Locale-formatted clock time, as shown in the Results tab. */
  time: string;
}

/**
 * Cap on stored history.
 *
 * localStorage is a few megabytes per origin and shared with the entries,
 * saved wheels and options. A long classroom session can produce hundreds of
 * spins; keeping the most recent 500 is far more than anyone reviews and still
 * leaves the quota comfortable.
 */
export const MAX_STORED_RESULTS = 500;

export function loadResults(prefix: string): ResultEntry[] {
  try {
    const raw = localStorage.getItem(`${prefix}_results`);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Validate rather than trust: this is the same storage a user can edit, and
    // a malformed record would render as "undefined" in the history list.
    return parsed
      .filter((r): r is ResultEntry =>
        !!r && typeof r === 'object' &&
        typeof (r as ResultEntry).name === 'string' &&
        typeof (r as ResultEntry).time === 'string')
      .slice(-MAX_STORED_RESULTS);
  } catch { return []; }
}

export function saveResults(prefix: string, results: ResultEntry[]): void {
  try {
    localStorage.setItem(`${prefix}_results`, JSON.stringify(results.slice(-MAX_STORED_RESULTS)));
  } catch { /* quota or private mode */ }
}

/** Quote a CSV field only when it needs it. */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * CSV of the spin history, oldest first, numbered.
 *
 * Oldest first on purpose: the on-screen list shows newest at the top because
 * that is what you look at during a draw, but a document of record reads in the
 * order things happened.
 */
export function resultsCsv(results: ResultEntry[], wheelTitle: string): string {
  const rows = results.map((r, i) => [
    String(i + 1),
    csvField(r.name),
    csvField(r.time),
    csvField(wheelTitle),
  ].join(','));
  return ['spin,winner,time,wheel', ...rows].join('\n');
}

/** Plain-text form for the clipboard — reads naturally when pasted into chat. */
export function resultsText(results: ResultEntry[], wheelTitle: string): string {
  const header = `${wheelTitle} — ${results.length} spin${results.length === 1 ? '' : 's'}`;
  return [header, ...results.map((r, i) => `${i + 1}. ${r.name} (${r.time})`)].join('\n');
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'wheel';
}

export function downloadResultsCsv(results: ResultEntry[], wheelTitle: string): void {
  const blob = new Blob([resultsCsv(results, wheelTitle)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug(wheelTitle)}-results.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
